import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useHistory } from 'react-router'
import {
    templateRegionIds,
    parseTemplate,
    selectionToRange,
} from '../../MicroPython/microPythonTemplate'
import { confirmVoiceoverGeneration } from '../voiceoverCredits'

const BACKEND_URL = process.env.REACT_APP_API_BASE
const STAGING_URL = `${BACKEND_URL}/api/staging`

const DEVICE_OPTIONS = ['peeCee', 'poki', 'toki']
// C requires a compile step before upload; Python (MicroPython) does not.
const ACTION_BUTTONS_PY = ['connect', 'run', 'upload', 'serial', 'console']
const ACTION_BUTTONS_C = ['connect', 'compile', 'run', 'upload', 'serial', 'console']

const emptyStep = () => ({
    title: '',
    instruction: '',
    activeRegionIds: [],
    enabledButtons: [],
    requiredAction: '',
    expected: {},
    hints: {},
    annotations: [],
})

const MicroPythonAdmin = ({ screenType = 'micropython' }) => {
    const { subId, screenId } = useParams()
    const history = useHistory()

    const isC = screenType === 'c'
    const defaultLanguage = isC ? 'c' : 'python'
    const languageLabel = isC ? 'C' : 'MicroPython'
    const actionButtons = isC ? ACTION_BUTTONS_C : ACTION_BUTTONS_PY
    const requiredActionOptions = ['', ...actionButtons]

    const apiPath = screenId
        ? `${screenType}/${subId}/${screenId}`
        : `${screenType}/${subId}`

    const [loading, setLoading] = useState(true)
    const [isGeneratingTTS, setIsGeneratingTTS] = useState(false)
    const templateRef = useRef(null)
    const codeSelectRef = useRef(null)

    const [formData, setFormData] = useState({
        version: '1.0',
        language: defaultLanguage,
        device: 'peeCee',
        deviceVersion: '1.0.0',
        meta: { title: '', description: '' },
        template: '',
        steps: [emptyStep()],
        totalSteps: 1,
        finalStepInstruction: '',
    })

    const regionIds = useMemo(
        () => templateRegionIds(formData.template),
        [formData.template]
    )

    const cleanCode = useMemo(
        () => parseTemplate(formData.template).code,
        [formData.template]
    )

    useEffect(() => {
        const fetchScreen = async () => {
            try {
                const res = await fetch(`${STAGING_URL}/${apiPath}`, {
                    credentials: 'include',
                })
                if (res.ok) {
                    const data = await res.json()
                    setFormData((prev) => ({
                        ...prev,
                        version: data.version || '1.0',
                        language: data.language || defaultLanguage,
                        device: data.device || 'peeCee',
                        deviceVersion: data.deviceVersion || '1.0.0',
                        meta: {
                            title: data.meta?.title || '',
                            description: data.meta?.description || '',
                        },
                        template: data.template || '',
                        steps:
                            Array.isArray(data.steps) && data.steps.length
                                ? data.steps.map((s) => ({
                                      ...emptyStep(),
                                      ...s,
                                      expected: s.expected || {},
                                      hints: s.hints || {},
                                      activeRegionIds: s.activeRegionIds || [],
                                      enabledButtons: s.enabledButtons || [],
                                      requiredAction: s.requiredAction || '',
                                      annotations: s.annotations || [],
                                  }))
                                : [emptyStep()],
                        finalStepInstruction: data.finalStepInstruction || '',
                    }))
                }
            } catch (err) {
                console.error('Error fetching micropython screen:', err)
            } finally {
                setLoading(false)
            }
        }
        fetchScreen()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subId, screenId])

    const setMeta = (key, value) =>
        setFormData((p) => ({ ...p, meta: { ...p.meta, [key]: value } }))
    const setField = (key, value) => setFormData((p) => ({ ...p, [key]: value }))

    const updateStep = (index, patch) =>
        setFormData((p) => {
            const steps = p.steps.map((s, i) =>
                i === index ? { ...s, ...patch } : s
            )
            return { ...p, steps }
        })

    const toggleInArray = (arr, value) =>
        arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value]

    const getExpected = (step, rid) => {
        const e = step.expected?.[rid]
        if (e && typeof e === 'object') {
            const dataType = e.dataType === 'number' ? 'number' : 'text'
            if (Array.isArray(e.accepted)) return { dataType, accepted: e.accepted }
            if (dataType === 'number')
                return {
                    dataType,
                    accepted: [
                        { op: e.op || '=', value: e.value ?? '', value2: e.value2 ?? '' },
                    ],
                }
            return { dataType, accepted: [e.value != null ? String(e.value) : ''] }
        }
        return {
            dataType: 'text',
            accepted: [typeof e === 'string' ? e : ''],
        }
    }
    const setExpected = (stepIndex, rid, next) =>
        updateStep(stepIndex, {
            expected: { ...formData.steps[stepIndex].expected, [rid]: next },
        })
    const setDataType = (stepIndex, rid, dataType) =>
        setExpected(stepIndex, rid, {
            dataType,
            accepted:
                dataType === 'number' ? [{ op: '=', value: '', value2: '' }] : [''],
        })
    const addAccepted = (stepIndex, rid) => {
        const cur = getExpected(formData.steps[stepIndex], rid)
        const item = cur.dataType === 'number' ? { op: '=', value: '', value2: '' } : ''
        setExpected(stepIndex, rid, { ...cur, accepted: [...cur.accepted, item] })
    }
    const removeAccepted = (stepIndex, rid, idx) => {
        const cur = getExpected(formData.steps[stepIndex], rid)
        setExpected(stepIndex, rid, {
            ...cur,
            accepted: cur.accepted.filter((_, i) => i !== idx),
        })
    }
    const updateAccepted = (stepIndex, rid, idx, value) => {
        const cur = getExpected(formData.steps[stepIndex], rid)
        setExpected(stepIndex, rid, {
            ...cur,
            accepted: cur.accepted.map((it, i) => (i === idx ? value : it)),
        })
    }

    const addAnnotationFromSelection = (stepIndex) => {
        const ta = codeSelectRef.current
        if (!ta) return
        const start = ta.selectionStart
        const end = ta.selectionEnd
        if (start == null || end == null || start === end) {
            alert(
                'Select the part of the code you want to explain in the "Selectable code" box above, then click this button.'
            )
            return
        }
        const range = selectionToRange(cleanCode, start, end)
        const text = cleanCode.slice(start, end)
        updateStep(stepIndex, {
            annotations: [
                ...(formData.steps[stepIndex].annotations || []),
                { ...range, note: '', text },
            ],
        })
    }
    const removeAnnotation = (stepIndex, annIndex) =>
        updateStep(stepIndex, {
            annotations: (formData.steps[stepIndex].annotations || []).filter(
                (_, i) => i !== annIndex
            ),
        })
    const updateAnnotation = (stepIndex, annIndex, patch) =>
        updateStep(stepIndex, {
            annotations: (formData.steps[stepIndex].annotations || []).map(
                (a, i) => (i === annIndex ? { ...a, ...patch } : a)
            ),
        })

    const addStep = () =>
        setFormData((p) => ({ ...p, steps: [...p.steps, emptyStep()] }))
    const removeStep = (index) =>
        setFormData((p) => ({
            ...p,
            steps: p.steps.filter((_, i) => i !== index),
        }))
    const moveStep = (index, dir) =>
        setFormData((p) => {
            const steps = [...p.steps]
            const j = index + dir
            if (j < 0 || j >= steps.length) return p
            ;[steps[index], steps[j]] = [steps[j], steps[index]]
            return { ...p, steps }
        })

    const markSelectionEditable = () => {
        const ta = templateRef.current
        if (!ta) return
        const start = ta.selectionStart
        const end = ta.selectionEnd
        if (start === end) {
            alert('Select the value in the code you want to make editable.')
            return
        }
        const id = window.prompt('Region id (e.g. pin, delay):')
        if (!id || !/^[A-Za-z_][\w-]*$/.test(id)) {
            if (id) alert('Invalid id. Use letters, numbers, _ or -.')
            return
        }
        const value = formData.template.slice(start, end)
        const next =
            formData.template.slice(0, start) +
            `{{${id}=${value}}}` +
            formData.template.slice(end)
        setField('template', next)
    }

    const buildPayload = () => ({
        version: formData.version || '1.0',
        language: formData.language || defaultLanguage,
        device: formData.device,
        deviceVersion: formData.deviceVersion,
        meta: { title: formData.meta.title, description: formData.meta.description },
        template: formData.template,
        totalSteps: formData.steps.length,
        steps: formData.steps.map((s, i) => ({
            stepNumber: i + 1,
            title: s.title,
            instruction: s.instruction,
            activeRegionIds: s.activeRegionIds,
            enabledButtons: s.enabledButtons,
            requiredAction: s.requiredAction || null,
            expected: s.expected,
            hints: s.hints,
            annotations: (s.annotations || []).map((a) => ({
                startLineNumber: Number(a.startLineNumber) || 1,
                startColumn: Number(a.startColumn) || 1,
                endLineNumber:
                    Number(a.endLineNumber) || Number(a.startLineNumber) || 1,
                endColumn: Number(a.endColumn) || Number(a.startColumn) + 1 || 2,
                note: a.note || '',
                text: a.text || '',
            })),
        })),
        ...(formData.finalStepInstruction
            ? { finalStepInstruction: formData.finalStepInstruction }
            : {}),
    })

    const handleSave = async () => {
        try {
            const res = await fetch(`${STAGING_URL}/${apiPath}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(buildPayload()),
                credentials: 'include',
            })
            if (!res.ok) throw new Error('Failed to save')
            alert(`${languageLabel} screen saved to staging!`)
        } catch (err) {
            console.error('Save error:', err)
            alert('Failed to save screen.')
        }
    }

    const handleGenerateVoiceover = async () => {
        if (!confirmVoiceoverGeneration(buildPayload())) return
        const currentScreenId = screenId || `${screenType}1`
        setIsGeneratingTTS(true)
        try {
            const apiKey = localStorage.getItem('freepikApiKey')
            const voiceId = localStorage.getItem('elevenVoiceId')
            if (!apiKey || !voiceId) {
                alert(
                    'Missing TTS configuration. Set the ElevenLabs API Key and Voice ID on the main admin page first.'
                )
                setIsGeneratingTTS(false)
                return
            }
            const res = await fetch(
                `${BACKEND_URL}/api/project/${subId}/generate-tts/${screenType}/${currentScreenId}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ apiKey, voiceId }),
                }
            )
            const data = await res.json()
            if (!res.ok) throw new Error(data.error || 'Failed to generate')
            alert(`✅ ${data.message}`)
        } catch (err) {
            console.error('TTS error:', err)
            alert(`❌ ${err.message}`)
        } finally {
            setIsGeneratingTTS(false)
        }
    }

    const handlePreview = () => {
        if (
            !window.confirm(
                'Preview uses the last SAVED version. Save first if you have unsaved changes. Continue?'
            )
        )
            return
        history.push(
            `/micropythonNew?preview=true&projectId=${subId}&type=${screenType}${
                screenId ? `&screenId=${screenId}` : ''
            }`
        )
    }

    if (loading) return <div style={{ padding: 24 }}>Loading…</div>

    const label = { fontWeight: 600, fontSize: 13, color: '#374151' }
    const input = {
        width: '100%',
        padding: '8px',
        border: '1px solid #d1d5db',
        borderRadius: 6,
        boxSizing: 'border-box',
    }
    const card = {
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        padding: 14,
        marginBottom: 12,
        background: '#fff',
    }

    return (
        <div
            style={{
                padding: 24,
                maxWidth: 980,
                margin: '0 auto',
                height: '100vh',
                overflowY: 'auto',
                boxSizing: 'border-box',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 16,
                }}
            >
                <h2 style={{ margin: 0 }}>
                    MicroPython Coding —{' '}
                    <span style={{ color: '#6d28d9' }}>
                        {screenId || subId}
                    </span>
                </h2>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={handlePreview} style={btn('#fff', '#374151')}>
                        Preview
                    </button>
                    <button
                        onClick={handleGenerateVoiceover}
                        disabled={isGeneratingTTS}
                        style={btn('#f59e0b', '#fff')}
                    >
                        {isGeneratingTTS ? 'Generating…' : 'Generate Voiceover'}
                    </button>
                    <button onClick={handleSave} style={btn('#6d28d9', '#fff')}>
                        Save
                    </button>
                </div>
            </div>

            <div style={card}>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 12,
                    }}
                >
                    <div>
                        <div style={label}>Title</div>
                        <input
                            style={input}
                            value={formData.meta.title}
                            onChange={(e) => setMeta('title', e.target.value)}
                        />
                    </div>
                    <div>
                        <div style={label}>Description</div>
                        <input
                            style={input}
                            value={formData.meta.description}
                            onChange={(e) =>
                                setMeta('description', e.target.value)
                            }
                        />
                    </div>
                    <div>
                        <div style={label}>Language</div>
                        <select style={input} value={defaultLanguage} disabled>
                            <option value={defaultLanguage}>{languageLabel}</option>
                        </select>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <div style={{ flex: 1 }}>
                            <div style={label}>Device</div>
                            <select
                                style={input}
                                value={formData.device}
                                onChange={(e) =>
                                    setField('device', e.target.value)
                                }
                            >
                                {DEVICE_OPTIONS.map((d) => (
                                    <option key={d} value={d}>
                                        {d}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div style={{ flex: 1 }}>
                            <div style={label}>Version</div>
                            <input
                                style={input}
                                value={formData.deviceVersion}
                                onChange={(e) =>
                                    setField('deviceVersion', e.target.value)
                                }
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div style={card}>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 8,
                    }}
                >
                    <div style={label}>
                        Code template — mark editable values as{' '}
                        <code>{'{{id=default}}'}</code>
                    </div>
                    <button
                        onClick={markSelectionEditable}
                        style={btn('#eef2ff', '#4338ca')}
                    >
                        Mark selection editable
                    </button>
                </div>
                <textarea
                    ref={templateRef}
                    style={{
                        ...input,
                        minHeight: 200,
                        fontFamily: 'monospace',
                        whiteSpace: 'pre',
                    }}
                    value={formData.template}
                    onChange={(e) => setField('template', e.target.value)}
                    placeholder={
                        'from machine import Pin\nled = Pin({{pin=13}}, Pin.OUT)\nled.value(1)'
                    }
                />
                <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
                    Detected regions:{' '}
                    {regionIds.length ? regionIds.join(', ') : '(none)'}
                </div>

                <div style={{ marginTop: 10 }}>
                    <div style={{ ...label, marginBottom: 4 }}>
                        Selectable code — highlight any part, then use “Add
                        explanation from selection” in a step
                    </div>
                    <textarea
                        ref={codeSelectRef}
                        readOnly
                        value={cleanCode}
                        spellCheck={false}
                        style={{
                            ...input,
                            minHeight: 150,
                            fontFamily: 'monospace',
                            fontSize: 13,
                            whiteSpace: 'pre',
                            background: '#0b1021',
                            color: '#e5e7eb',
                        }}
                    />
                </div>
            </div>

            <h3>Steps</h3>
            {formData.steps.map((s, i) => (
                <div key={i} style={card}>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: 8,
                        }}
                    >
                        <strong>Step {i + 1}</strong>
                        <div style={{ display: 'flex', gap: 6 }}>
                            <button onClick={() => moveStep(i, -1)} style={miniBtn}>
                                ↑
                            </button>
                            <button onClick={() => moveStep(i, 1)} style={miniBtn}>
                                ↓
                            </button>
                            <button
                                onClick={() => removeStep(i)}
                                style={{ ...miniBtn, color: '#dc2626' }}
                            >
                                ✕
                            </button>
                        </div>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                        <div style={label}>Title</div>
                        <input
                            style={input}
                            value={s.title}
                            onChange={(e) =>
                                updateStep(i, { title: e.target.value })
                            }
                        />
                    </div>
                    <div style={{ marginBottom: 8 }}>
                        <div style={label}>Instruction (voiceover)</div>
                        <textarea
                            style={{ ...input, minHeight: 60 }}
                            value={s.instruction}
                            onChange={(e) =>
                                updateStep(i, { instruction: e.target.value })
                            }
                        />
                    </div>

                    <div style={{ marginBottom: 8 }}>
                        <div style={label}>Editable regions this step</div>
                        {regionIds.length === 0 && (
                            <span style={{ color: '#9ca3af', fontSize: 13 }}>
                                Add tokens to the template first.
                            </span>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {regionIds.map((rid) => {
                                const isActive = s.activeRegionIds.includes(rid)
                                const exp = getExpected(s, rid)
                                return (
                                    <div
                                        key={rid}
                                        style={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            alignItems: 'center',
                                            gap: 8,
                                            fontSize: 13,
                                            background: isActive
                                                ? '#f8fafc'
                                                : 'transparent',
                                            border: isActive
                                                ? '1px solid #e5e7eb'
                                                : '1px solid transparent',
                                            borderRadius: 6,
                                            padding: isActive ? 8 : '2px 0',
                                        }}
                                    >
                                        <label
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 4,
                                                fontWeight: 600,
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isActive}
                                                onChange={() =>
                                                    updateStep(i, {
                                                        activeRegionIds:
                                                            toggleInArray(
                                                                s.activeRegionIds,
                                                                rid
                                                            ),
                                                    })
                                                }
                                            />
                                            {rid}
                                        </label>
                                        {isActive && (
                                            <>
                                                <select
                                                    style={{
                                                        ...input,
                                                        width: 90,
                                                        padding: '4px 6px',
                                                    }}
                                                    value={exp.dataType}
                                                    onChange={(e) =>
                                                        setDataType(
                                                            i,
                                                            rid,
                                                            e.target.value
                                                        )
                                                    }
                                                >
                                                    <option value="text">
                                                        text
                                                    </option>
                                                    <option value="number">
                                                        number
                                                    </option>
                                                </select>

                                                <span
                                                    style={{
                                                        fontSize: 12,
                                                        color: '#6b7280',
                                                    }}
                                                >
                                                    accept any of:
                                                </span>

                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexWrap: 'wrap',
                                                        gap: 6,
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    {exp.accepted.map(
                                                        (opt, oi) => (
                                                            <div
                                                                key={oi}
                                                                style={{
                                                                    display:
                                                                        'flex',
                                                                    alignItems:
                                                                        'center',
                                                                    gap: 4,
                                                                }}
                                                            >
                                                                {exp.dataType ===
                                                                'text' ? (
                                                                    <input
                                                                        style={{
                                                                            ...input,
                                                                            width: 120,
                                                                            padding:
                                                                                '4px 6px',
                                                                        }}
                                                                        placeholder="e.g. true"
                                                                        value={
                                                                            opt ||
                                                                            ''
                                                                        }
                                                                        onChange={(
                                                                            e
                                                                        ) =>
                                                                            updateAccepted(
                                                                                i,
                                                                                rid,
                                                                                oi,
                                                                                e
                                                                                    .target
                                                                                    .value
                                                                            )
                                                                        }
                                                                    />
                                                                ) : (
                                                                    <>
                                                                        <select
                                                                            style={{
                                                                                ...input,
                                                                                width: 70,
                                                                                padding:
                                                                                    '4px 4px',
                                                                            }}
                                                                            value={
                                                                                opt.op ||
                                                                                '='
                                                                            }
                                                                            onChange={(
                                                                                e
                                                                            ) =>
                                                                                updateAccepted(
                                                                                    i,
                                                                                    rid,
                                                                                    oi,
                                                                                    {
                                                                                        ...opt,
                                                                                        op: e
                                                                                            .target
                                                                                            .value,
                                                                                    }
                                                                                )
                                                                            }
                                                                        >
                                                                            <option value="=">
                                                                                =
                                                                            </option>
                                                                            <option value=">">
                                                                                &gt;
                                                                            </option>
                                                                            <option value="<">
                                                                                &lt;
                                                                            </option>
                                                                            <option value=">=">
                                                                                ≥
                                                                            </option>
                                                                            <option value="<=">
                                                                                ≤
                                                                            </option>
                                                                            <option value="range">
                                                                                range
                                                                            </option>
                                                                        </select>
                                                                        <input
                                                                            type="number"
                                                                            style={{
                                                                                ...input,
                                                                                width: 70,
                                                                                padding:
                                                                                    '4px 6px',
                                                                            }}
                                                                            placeholder={
                                                                                opt.op ===
                                                                                'range'
                                                                                    ? 'min'
                                                                                    : 'value'
                                                                            }
                                                                            value={
                                                                                opt.value ||
                                                                                ''
                                                                            }
                                                                            onChange={(
                                                                                e
                                                                            ) =>
                                                                                updateAccepted(
                                                                                    i,
                                                                                    rid,
                                                                                    oi,
                                                                                    {
                                                                                        ...opt,
                                                                                        value: e
                                                                                            .target
                                                                                            .value,
                                                                                    }
                                                                                )
                                                                            }
                                                                        />
                                                                        {opt.op ===
                                                                            'range' && (
                                                                            <input
                                                                                type="number"
                                                                                style={{
                                                                                    ...input,
                                                                                    width: 70,
                                                                                    padding:
                                                                                        '4px 6px',
                                                                                }}
                                                                                placeholder="max"
                                                                                value={
                                                                                    opt.value2 ||
                                                                                    ''
                                                                                }
                                                                                onChange={(
                                                                                    e
                                                                                ) =>
                                                                                    updateAccepted(
                                                                                        i,
                                                                                        rid,
                                                                                        oi,
                                                                                        {
                                                                                            ...opt,
                                                                                            value2: e
                                                                                                .target
                                                                                                .value,
                                                                                        }
                                                                                    )
                                                                                }
                                                                            />
                                                                        )}
                                                                    </>
                                                                )}
                                                                {exp.accepted
                                                                    .length >
                                                                    1 && (
                                                                    <button
                                                                        onClick={() =>
                                                                            removeAccepted(
                                                                                i,
                                                                                rid,
                                                                                oi
                                                                            )
                                                                        }
                                                                        style={{
                                                                            ...miniBtn,
                                                                            color: '#dc2626',
                                                                            padding:
                                                                                '2px 6px',
                                                                        }}
                                                                        title="Remove option"
                                                                    >
                                                                        ✕
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )
                                                    )}
                                                    <button
                                                        onClick={() =>
                                                            addAccepted(i, rid)
                                                        }
                                                        style={{
                                                            ...miniBtn,
                                                            color: '#1d4ed8',
                                                            fontWeight: 700,
                                                        }}
                                                        title="Add another accepted value"
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                                <span
                                                    style={{
                                                        fontSize: 11,
                                                        color: '#9ca3af',
                                                        width: '100%',
                                                    }}
                                                >
                                                    Learner passes if their value
                                                    matches any option. Leave all
                                                    blank = editable but not
                                                    graded.
                                                </span>
                                            </>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            gap: 16,
                            flexWrap: 'wrap',
                            alignItems: 'flex-end',
                        }}
                    >
                        <div>
                            <div style={label}>Enabled buttons</div>
                            <div style={{ display: 'flex', gap: 10 }}>
                                {actionButtons.map((b) => (
                                    <label
                                        key={b}
                                        style={{ fontSize: 13, display: 'flex', gap: 3 }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={s.enabledButtons.includes(b)}
                                            onChange={() => {
                                                const newEnabled = toggleInArray(s.enabledButtons, b)
                                                const updates = { enabledButtons: newEnabled }
                                                if (s.requiredAction === b) {
                                                    updates.requiredAction = ''
                                                }
                                                updateStep(i, updates)
                                            }}
                                        />
                                        {b}
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div>
                            <div style={label}>Required action (to advance)</div>
                            <select
                                style={{ ...input, width: 160 }}
                                value={s.requiredAction}
                                onChange={(e) => {
                                    const newReq = e.target.value
                                    const updates = { requiredAction: newReq }
                                    if (newReq && !s.enabledButtons.includes(newReq)) {
                                        updates.enabledButtons = [...s.enabledButtons, newReq]
                                    }
                                    updateStep(i, updates)
                                }}
                            >
                                {requiredActionOptions.map((a) => (
                                    <option key={a} value={a}>
                                        {a || 'none'}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div
                        style={{
                            marginTop: 12,
                            borderTop: '1px dashed #e5e7eb',
                            paddingTop: 10,
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: 6,
                            }}
                        >
                            <div style={label}>
                                Explanation highlights (what the code does)
                            </div>
                            <button
                                onClick={() => addAnnotationFromSelection(i)}
                                style={btn('#eff6ff', '#1d4ed8')}
                            >
                                + Add explanation from selection
                            </button>
                        </div>
                        {(s.annotations || []).length === 0 && (
                            <div style={{ fontSize: 13, color: '#9ca3af' }}>
                                Select code in the “Selectable code” box above,
                                then click the button.
                            </div>
                        )}
                        {(s.annotations || []).map((a, ai) => (
                            <div
                                key={ai}
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 8,
                                    alignItems: 'center',
                                    marginBottom: 8,
                                    background: '#f8fafc',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 6,
                                    padding: 8,
                                }}
                            >
                                <code
                                    title={`Line ${a.startLineNumber}${
                                        a.endLineNumber &&
                                        a.endLineNumber !== a.startLineNumber
                                            ? `–${a.endLineNumber}`
                                            : ''
                                    }`}
                                    style={{
                                        background: '#0b1021',
                                        color: '#93c5fd',
                                        padding: '3px 8px',
                                        borderRadius: 4,
                                        fontSize: 12,
                                        maxWidth: 260,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {a.text || `L${a.startLineNumber}`}
                                </code>
                                <input
                                    style={{ ...input, flex: 1, minWidth: 200 }}
                                    placeholder="What does this code do?"
                                    value={a.note}
                                    onChange={(e) =>
                                        updateAnnotation(i, ai, {
                                            note: e.target.value,
                                        })
                                    }
                                />
                                <button
                                    onClick={() => removeAnnotation(i, ai)}
                                    style={{ ...miniBtn, color: '#dc2626' }}
                                >
                                    ✕
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
            <button onClick={addStep} style={btn('#eef2ff', '#4338ca')}>
                + Add step
            </button>

            <div style={{ ...card, marginTop: 16 }}>
                <div style={label}>Final instruction (optional, spoken last)</div>
                <input
                    style={input}
                    value={formData.finalStepInstruction}
                    onChange={(e) =>
                        setField('finalStepInstruction', e.target.value)
                    }
                />
            </div>
        </div>
    )
}

const btn = (bg, color) => ({
    padding: '8px 14px',
    borderRadius: 6,
    border: '1px solid #d1d5db',
    background: bg,
    color,
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: 13,
})
const miniBtn = {
    padding: '2px 8px',
    borderRadius: 4,
    border: '1px solid #d1d5db',
    background: '#fff',
    cursor: 'pointer',
}

export default MicroPythonAdmin
